import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import type { ModifiedCoveragePlanDatesPeriod } from "@/model/coverage-years";
import type {
  SpendSummary,
  SpendSummaryAccumulator,
  SpendSummaryAccumulatorWithTier,
  SpendSummaryNetwork,
  SpendSummaryTier,
} from "@/model/spend-summary";

import { DEFAULT_NETWORK_TYPE, QueryKeys } from "@/constants";
import type { IUserInfo } from "@/hooks/use-user-info";
import { useIsFeatureEnabled } from "@/hooks/use-features";
import ScreenRouteNames from "@/constants/screen-route-names";

import { fetchSpendSummary } from "@/services/spend-summary";
import type { PlanCoverageInfo } from "@/model/demographics";
import { useMemo } from "react";

export const useSpendSummary = (
  userInfo: IUserInfo,
  selectedCoverageYear?: ModifiedCoveragePlanDatesPeriod | null,
  activeCoverage?: PlanCoverageInfo,
): UseQueryResult<SpendSummary, Error> => {
  const spendSummaryQueryKey = useMemo(() => {
    return [
      QueryKeys.SPEND_SUMMARY,
      activeCoverage?.claimCoverageCode,
      selectedCoverageYear?.startDate,
      selectedCoverageYear?.endDate,
      selectedCoverageYear?.id,
    ];
  }, [
    activeCoverage?.claimCoverageCode,
    selectedCoverageYear?.startDate,
    selectedCoverageYear?.endDate,
    selectedCoverageYear?.id,
  ]);

  const isSpendSummaryFeatureEnabled = useIsFeatureEnabled(
    ScreenRouteNames.SPEND_SUMMARY.path,
  );

  return useQuery<SpendSummary, Error>({
    queryKey: spendSummaryQueryKey,
    queryFn: () =>
      fetchSpendSummary(userInfo, selectedCoverageYear, activeCoverage),
    retry: false,
    retryOnMount: false,
    enabled:
      Boolean(userInfo?.accessToken) &&
      Boolean(userInfo?.memberId) &&
      isSpendSummaryFeatureEnabled,
  });
};

export const useSpendSummaryWithCoverage = (
  userInfo: IUserInfo,
  activeCoverage?: PlanCoverageInfo,
  selectedCoverageYear?: ModifiedCoveragePlanDatesPeriod | null,
) => {
  const spendQuery = useSpendSummary(
    userInfo,
    selectedCoverageYear,
    activeCoverage,
  );

  const accumulatorInfo = (
    memberId: string,
    tiers: { name: string }[],
  ): SpendSummaryAccumulatorWithTier[] => {
    const networkData = spendQuery.data?.data?.find(
      (item: SpendSummaryNetwork) =>
        item.networkType === DEFAULT_NETWORK_TYPE.NETWORK_NAME,
    );
    if (networkData) {
      const benefitType = networkData.PTPlanBenefitType;
      const memberAccumulatorInInNetwork: SpendSummaryAccumulatorWithTier[] =
        [];
      for (const tier of tiers) {
        let memberAcccum: SpendSummaryAccumulator | undefined = undefined;
        let memberAcccumWithTier: SpendSummaryAccumulatorWithTier | undefined =
          undefined;
        if (benefitType === "self") {
          memberAcccum = networkData.data
            ?.find((item: SpendSummaryTier) => item?.tierType === tier?.name)
            ?.accumulator.find(
              (item: SpendSummaryAccumulator) => item?.memberId === memberId,
            );
        } else {
          memberAcccum = networkData.data
            ?.find((item: SpendSummaryTier) => item?.tierType === tier.name)
            ?.accumulator.find(
              (item: SpendSummaryAccumulator) => item?.type === "Family",
            );
        }
        if (memberAcccum) {
          memberAcccumWithTier = {
            ...memberAcccum,
            tierType: tier.name,
          };
          memberAccumulatorInInNetwork.push(memberAcccumWithTier);
        }
      }
      return memberAccumulatorInInNetwork;
    }
    return [];
  };

  return {
    // Data
    spendQuery,

    // Functions
    accumulatorInfo,
  };
};
